import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';

export type AlertSeverity = 'error' | 'warning' | 'info' | 'success';

@Component({
  selector: 'app-alert',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="alert" [ngClass]="'alert--' + severity">
      <div class="alert__content">
        <div class="alert__icon">
          <i [class]="iconClass"></i>
        </div>
        <div class="alert__body">
          <h4 *ngIf="title" class="alert__title">{{ title }}</h4>
          <p *ngIf="message" class="alert__message">{{ message }}</p>
          <ng-content></ng-content>
        </div>
      </div>
      <div *ngIf="showActions" class="alert__actions">
        <ng-content select="[alert-actions]"></ng-content>
      </div>
    </div>
  `,
  styles: [`
    .alert {
      border-radius: 12px;
      padding: 16px;
      border: 1px solid;
    }

    .alert__content {
      display: flex;
      align-items: flex-start;
      gap: 12px;
    }

    .alert__icon {
      flex-shrink: 0;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;

      i {
        font-size: 20px;
      }
    }

    .alert__body {
      flex: 1;
      min-width: 0;
    }

    .alert__title {
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
      font-size: 14px;
      font-weight: 600;
      margin: 0 0 4px;
    }

    .alert__message {
      font-size: 14px;
      margin: 0;
      line-height: 1.5;
    }

    .alert__actions {
      margin-top: 16px;
      padding-top: 16px;
      border-top: 1px solid;
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
    }

    // Error
    .alert--error {
      background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
      border-color: #fecaca;

      .alert__icon i { color: #dc2626; }
      .alert__title { color: #991b1b; }
      .alert__message { color: #b91c1c; }
      .alert__actions { border-color: #fecaca; }
    }

    // Warning
    .alert--warning {
      background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
      border-color: #fde68a;

      .alert__icon i { color: #d97706; }
      .alert__title { color: #92400e; }
      .alert__message { color: #b45309; }
      .alert__actions { border-color: #fde68a; }
    }

    // Info
    .alert--info {
      background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
      border-color: #bfdbfe;

      .alert__icon i { color: #2563eb; }
      .alert__title { color: #1e40af; }
      .alert__message { color: #1d4ed8; }
      .alert__actions { border-color: #bfdbfe; }
    }

    // Success
    .alert--success {
      background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
      border-color: #bbf7d0;

      .alert__icon i { color: #16a34a; }
      .alert__title { color: #166534; }
      .alert__message { color: #15803d; }
      .alert__actions { border-color: #bbf7d0; }
    }

    // Dark mode support
    :host-context(.dark) {
      .alert--error {
        background: rgba(127, 29, 29, 0.2);
        border-color: rgba(185, 28, 28, 0.4);
        .alert__icon i { color: #f87171; }
        .alert__title { color: #fecaca; }
        .alert__message { color: #fca5a5; }
      }

      .alert--warning {
        background: rgba(120, 53, 15, 0.2);
        border-color: rgba(180, 83, 9, 0.4);
        .alert__icon i { color: #fbbf24; }
        .alert__title { color: #fde68a; }
        .alert__message { color: #fcd34d; }
      }

      .alert--info {
        background: rgba(30, 64, 175, 0.2);
        border-color: rgba(37, 99, 235, 0.4);
        .alert__icon i { color: #60a5fa; }
        .alert__title { color: #bfdbfe; }
        .alert__message { color: #93c5fd; }
      }

      .alert--success {
        background: rgba(22, 101, 52, 0.2);
        border-color: rgba(22, 163, 74, 0.4);
        .alert__icon i { color: #4ade80; }
        .alert__title { color: #bbf7d0; }
        .alert__message { color: #86efac; }
      }
    }
  `]
})
export class AlertComponent {
  @Input() severity: AlertSeverity = 'info';
  @Input() title?: string;
  @Input() message?: string;
  @Input() icon?: string;
  @Input() showActions = false;

  get iconClass(): string {
    if (this.icon) return this.icon;

    switch (this.severity) {
      case 'error': return 'pi pi-exclamation-circle';
      case 'warning': return 'pi pi-exclamation-triangle';
      case 'success': return 'pi pi-check-circle';
      case 'info':
      default: return 'pi pi-info-circle';
    }
  }
}
